import api from "../../api/api";
import type {
  Product,
  ProductCardProps,
} from "../../types/types";
import i18n from "i18next"; // i18n konfiguratsiyasini import qilamiz

let productByIdCache: { [key: string]: Product } = {};
let productByIdCacheTime: { [key: string]: number } = {};
let relatedProductsCache: { [key: string]: ProductCardProps[] } = {};
let relatedProductsCacheTime: { [key: string]: number } = {};
let allProductsCache: ProductCardProps[] = [];
let allProductsCacheTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getProductById = async (id: number): Promise<Product> => {
  const now = Date.now();
  const cacheKey = id.toString();

  if (
    productByIdCache[cacheKey] &&
    now - productByIdCacheTime[cacheKey] < CACHE_DURATION
  ) {
    return productByIdCache[cacheKey];
  }

  try {
    const response = await api.get(`/products/product/${id}/`);
    if (!response.data) {
      console.log(i18n.t("productApi.noProductData"), response.data);
      throw new Error(i18n.t("productApi.productNotFound"));
    }
    // Process images
    let images = response.data.images;
    if (typeof images === "string") {
      images = images.split(",").filter((img: string) => img.trim() !== "");
    } else if (!Array.isArray(images)) {
      images = [];
    }

    // Process features
    const features = response.data.features
      .filter((feature: any) => feature.count > 0)
      .map((feature: any) => ({
        ...feature,
        images: Array.isArray(feature.images)
          ? feature.images.filter((img: string) => img.trim() !== "")
          : typeof feature.images === "string"
          ? feature.images.split(",").filter((img: string) => img.trim() !== "")
          : [],
      }));

    const processedData = {
      ...response.data,
      images: Array.isArray(images) ? images : [],
      features,
    };

    productByIdCache[cacheKey] = processedData;
    productByIdCacheTime[cacheKey] = now;

    return processedData;
  } catch (error) {
    console.warn(i18n.t("productApi.errorFetchingProduct"), error);
    throw error; // Xatoni yuqoriga uzatamiz
  }
};

export const getRelatedProducts = async (
  id: string
): Promise<ProductCardProps[]> => {
  const now = Date.now();
  const cacheKey = id;

  if (
    relatedProductsCache[cacheKey] &&
    now - relatedProductsCacheTime[cacheKey] < CACHE_DURATION
  ) {
    return relatedProductsCache[cacheKey];
  }

  try {
    const response = await api.get(`/products/related-products/${id}/`);
    if (!response.data || !Array.isArray(response.data)) {
      console.warn(i18n.t("productApi.noRelatedProductsData"), response.data);
      return []; // fallback bo‘sh array
    }
    const processedData = response.data.map((product: any) => ({
      id: product.id,
      title: product.title,
      price: product.price,
      image: Array.isArray(product.images) ? product.images : [],
      discount: product.discount || null,
      discount_price: product.discount_price || null,
      sold_count: product.sold_count ?? null,
      view_count: product.view_count ?? null,
      rating: product.rating ?? null,
    }));

    relatedProductsCache[cacheKey] = processedData;
    relatedProductsCacheTime[cacheKey] = now;

    return processedData;
} catch (error) {
    console.warn(i18n.t("productApi.errorFetchingRelatedProducts"), error);
    return []; // fallback bo'sh array
  }
};

export const getAllProducts = async (page: any) => {
  const response = await api.get(`/products/all-products/?page=${page}`);
  return response.data;
};

export const getAllSearchResults = async ({
  queryKey,
  pageParam = 1,
}: {
  queryKey: string[]; // ✅ [string, string] emas, string[]
  pageParam?: number;
}) => {
  const searchQuery = queryKey[1]; // ✅ ikkinchi elementni qo‘lda olamiz
  const res = await api.get(
    `/products/search-product/?q=${encodeURIComponent(searchQuery)}&page=${pageParam}`
  );
  return res.data;
};
